import React, { useState } from 'react';

// Material UI
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from 'tss-react/mui';
import { green, red } from '@mui/material/colors';

// Icons
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

// Hooks
import SearchProduct  from '../../services/searchProductsService';
import useUser from '../../hooks/user/useUser';

// Components
import Modal from '../modal'
import useNotify from '../../hooks/notify';
import Spinner from '../spinner/'
import TextField from '../inputs/input';
import Divider from '@mui/material/Divider';
import Table from '../../components/table/index';

const columns = [
    { id: 'refId', label: 'SKU' },
    { id: 'name', label: 'Descripcion' },
    { id: 'color', label: 'Color' },
    { id: 'quantity', label: 'Cantidad', align: 'right' }
]

const useStyles = makeStyles()(() => ({
    iconDelete: {
        color: red[300]
    },
    iconAdd: {
        color: green[300]
    }
}))

const ModalEscanner = ({ open, onSave, onClose }) => {  
    const { classes } = useStyles();
    const [productScanner, setProductScanner] = useState([]);
    const [loading, setLoading] = useState(false);
    const {branch} = useUser();
    const [timerId, setTimerId] = useState();
    const [disabledInput, setDisabledInput] = useState();
    const [refId, setRefId] = useState();
    const {Notify, variants}  = useNotify();

    const scannerProducts = async (code, color) => {
        setLoading(true);
        const searchResult = await SearchProduct
            .Search({ code, branchCode: branch.code, country: branch.country })
            .finally(() => setLoading(false));
        
        if(searchResult.result.length === 1) 
            return searchResult.result[0];
        else {
            const filter = searchResult.result.find(i => i.variants.some(s => s.refId === code + "%" + color));
            if(filter !== undefined) {
                return filter;
            }
            Notify(`No se encontro el producto con el codigo ${code}`, variants.ERROR);
        }
    }

    const handleInputChange = ( event ) => {
        if(disabledInput)
            return;

        const refId = event.target.value
            ?.replace(/\s/g, '')
            .toUpperCase();

        setRefId(refId);
    }

    const handleInputKeyUp = ( event ) => {
        if(timerId)
            clearTimeout(timerId);

        if (event.keyCode === 13 || event.code === 'Enter') {
            if(!refId)
                return;

            setDisabledInput(true);
    
            if(productScanner.some(product => product.refId === refId)) {            
                updateProduct(refId);
                return;
            }
    
            setTimerId( setTimeout(() => {
                addProduct(refId);
                setDisabledInput(false);
            }, 800) );
        }
    }

    const updateProduct = (refId) => {
        const products = productScanner.map(product => {
            if(product.refId !== refId) return product;
            return {
                ...product, quantity : product.quantity + 1 
            }
        })
        setProductScanner(products);
        setRefId("");
        setDisabledInput(false);
    }

    const addProduct = async (refId) => {
        const masterSku = refId.split("%")[0];
        const color = refId.split("%")[1];
        const product = await scannerProducts(masterSku, color);

        if(!product) return;

        const variant = product.variants.find( variant => variant.refId === refId);

        if(!variant) {
            Notify(`No se encontro el color ${color} en el codigo ${masterSku}`, variants.ERROR);
            return;
        }

        setProductScanner([...productScanner, {
            ...variant, 
            masterProductId: product.id, 
            quantity : 1
        }]);
        setRefId("");
    }

    const MapProductTable = () =>
        productScanner.map(product => ({ 
            refId: product.refId,
            color: product.color.name, 
            name: product.name,
            quantity: product.quantity
        }))

    const handleChangeQuantity = ( refId, value = 1 ) => {
        const products = productScanner
            .map(product => {
                if(product.refId !== refId) return product;
                return { ...product, quantity : product.quantity + value }
            })
            .filter(product => product.quantity > 0);

        setProductScanner(products);
    }

    return (
        <Modal
            fullWidth
            fullScreen={false}
            disableEscapeKeyDown
            open={open}
            maxWidth='md'
            customButtons={ () => 
                <DialogActions>
                    <Button 
                        color="primary" 
                        variant="outlined" 
                        onClick={onClose}
                    >
                        Cerrar
                    </Button>
                    <Button 
                        color="primary" 
                        variant="contained" 
                        onClick={() => {
                            onSave(productScanner, branch.priceListId)
                            onClose()
                        }}
                    >
                        Agregar al Carrito
                    </Button>
                </DialogActions>
            }
        >
            {   loading &&
                <Spinner lockScreen message="Buscando Producto.." />
            }
            <Grid container spacing={1} alignItems='center'>
                <Grid size={6}>
                    <Typography
                        component='h2'
                        color='primary'
                        variant='h5'
                    >
                        Detalles
                    </Typography>                            
                </Grid>
                <Grid size={6}>
                    <TextField
                        fullWidth
                        autoFocus
                        name='code'
                        label='Sku'
                        size='small'
                        value={ refId }
                        onChange={ handleInputChange }
                        onKeyUp={ handleInputKeyUp }
                        helperText="Presiona 'Enter' para ingresar el codigo"
                    />
                </Grid> 
                <Grid size={12}>
                    <Divider />
                </Grid>
                <Grid size={12}>
                    <Table
                        rows={ MapProductTable() }
                        columns={columns}
                        actions={(item) =>
                            <>
                                <IconButton
                                    size='small'
                                    color='primary'
                                    className={classes.iconDelete}
                                    onClick={() => handleChangeQuantity(item.refId, -1)}
                                >
                                    <RemoveIcon />
                                </IconButton>
                                <IconButton
                                    size='small'
                                    color='primary'
                                    className={classes.iconAdd}
                                    onClick={() => handleChangeQuantity(item.refId, 1)}
                                >
                                    <AddIcon />
                                </IconButton>
                            </>
                        }
                    />
                </Grid>
            </Grid>
        </Modal>
    );
}

export default ModalEscanner;